import {
  Box,
  Button,
  Collapsible,
  Flex,
  Image,
  Menu,
  Portal,
  Stack,
  Text,
  Textarea
} from '@chakra-ui/react';
import { ArrowCounterClockwise, Check, DotsThree, Plus, Trash, X } from '@phosphor-icons/react';
import { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import aiIcon from '@/assets/icon/ai-icon.svg';
import { AlertsAndFollowUpQuestion } from '@/types/AnalysisResult';
import {
  FollowUpQuestionStatus,
  getFollowUpQuestionStatusColor,
  getFollowUpQuestionStatusLabel
} from '@/types/enums';

interface FollowUpQuestion {
  question: string;
  question_type: 'essay_question';
}

interface FormData extends AlertsAndFollowUpQuestion {
  follow_up_questions: FollowUpQuestion[];
}

interface FollowUpItemBoxProps {
  index: number;
  followUpItem: AlertsAndFollowUpQuestion;
  onUpdateItem?: (index: number, item: AlertsAndFollowUpQuestion | null) => Promise<boolean>;
  readonly?: boolean;
  withBackground?: boolean;
  evaluate?: boolean;
  withStatus?: boolean;
  withFeedback?: boolean;
  collapsible?: boolean;
}

export default function FollowUpItemBox({
  index,
  followUpItem,
  onUpdateItem,
  readonly = false,
  withBackground = true,
  evaluate = false,
  withStatus = false,
  withFeedback = false,
  collapsible = false
}: FollowUpItemBoxProps) {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    defaultValues: followUpItem as FormData,
    mode: 'onBlur'
  });

  const [editMode, setEditMode] = useState(readonly ? false : followUpItem.title === '');
  const [submitError, setSubmitError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setOpen] = useState(collapsible ? false : true);

  const addNewQuestion = useCallback(async () => {
    const currentQuestions = watch('follow_up_questions');
    reset({
      ...watch(),
      follow_up_questions: [...currentQuestions, { question: '', question_type: 'essay_question' }]
    });
  }, [watch, reset]);

  const handleDeleteItem = useCallback(async () => {
    if (!onUpdateItem) return;

    setIsLoading(true);
    try {
      await onUpdateItem(index, null);
    } catch (error) {
      console.error('Failed to delete follow up item:', error);
      setSubmitError(true);
    } finally {
      setIsLoading(false);
    }
  }, [onUpdateItem, index]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (!data.follow_up_questions || data.follow_up_questions.length === 0) {
        await addNewQuestion();
        await trigger();
        return;
      }

      if (onUpdateItem) {
        setIsLoading(true);
        try {
          const updatedData = {
            ...data,
            status: data.status || FollowUpQuestionStatus.Pending
          };
          const success = await onUpdateItem(index, updatedData);
          if (success) {
            setSubmitError(false);
            setEditMode(false);
          } else {
            setSubmitError(true);
          }
        } catch (error) {
          console.error('Failed to update follow up item:', error);
          setSubmitError(true);
        } finally {
          setIsLoading(false);
        }
      } else {
        setEditMode(false);
      }
    },
    [addNewQuestion, index, onUpdateItem, trigger]
  );

  const handleEvaluate = useCallback(
    async (status: FollowUpQuestionStatus) => {
      if (!onUpdateItem) return;

      setIsLoading(true);

      const updatedData = {
        ...followUpItem,
        status
      };

      try {
        await onUpdateItem(index, updatedData);
      } catch (error) {
        console.error('Failed to update follow up item status:', error);
        setSubmitError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [onUpdateItem, index, followUpItem]
  );

  const handleCancel = useCallback(() => {
    reset(followUpItem as FormData);
    if (followUpItem.title === '' && onUpdateItem) {
      onUpdateItem(index, null);
    }
    setEditMode(false);
    setSubmitError(false);
  }, [followUpItem, index, onUpdateItem, reset]);

  const isDisabled = isLoading || isSubmitting;

  // Memoize computed values
  const suggestUpdateStatus = useMemo(
    () =>
      followUpItem.status === FollowUpQuestionStatus.Pending ||
      (followUpItem.status === FollowUpQuestionStatus.Resolved && !followUpItem.resolved) ||
      (followUpItem.status === FollowUpQuestionStatus.NotFit && followUpItem.resolved),
    [followUpItem.status, followUpItem.resolved]
  );

  const statusColor = useMemo(
    () => getFollowUpQuestionStatusColor(followUpItem.status),
    [followUpItem.status]
  );

  // Memoize handlers to prevent unnecessary re-renders
  const handleEditClick = useCallback(() => setEditMode(true), []);
  const handleCollapsibleChange = useCallback(
    ({ open }: { open: boolean }) => {
      if (!collapsible) return;
      if (followUpItem.status === FollowUpQuestionStatus.Pending) return;
      if (editMode) {
        setOpen(true);
        return;
      }
      setOpen(open);
    },
    [collapsible, editMode, followUpItem.status]
  );

  return (
    <Box
      border={withBackground ? '1px solid' : 'none'}
      borderColor={withBackground ? 'neutrals.200' : 'transparent'}
      borderRadius="8px"
      bg={withBackground ? 'neutrals.50' : 'transparent'}
      w="100%"
      h="100%"
      py="16px"
      transition="all 0.2s ease-in-out"
      _hover={{ boxShadow: 'sm' }}
      role="region"
      aria-label="Follow up item"
    >
      <Collapsible.Root h="100%" open={isOpen} onOpenChange={handleCollapsibleChange}>
        <Collapsible.Trigger w="100%" cursor="pointer">
          <Box
            w="100%"
            px="24px"
            py={readonly ? '8px' : '0'}
            borderRadius="8px"
            bg={withBackground ? 'neutrals.50' : 'transparent'}
          >
            <Flex justifyContent="space-between" alignItems="center">
              {editMode ? (
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: 'Title is required' }}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      autoresize
                      fontSize="16px"
                      maxH="40px"
                      bg="background.secondary"
                      borderRadius="8px"
                      borderColor={errors.title ? 'error.500' : 'neutrals.200'}
                      borderWidth="1px"
                      placeholder="Enter the title of the follow up item"
                      aria-label="Follow up item title"
                      aria-invalid={!!errors.title}
                      aria-describedby={errors.title ? 'title-error' : undefined}
                      disabled={isDisabled}
                    />
                  )}
                />
              ) : (
                <Text
                  color="neutrals.800"
                  fontSize="18px"
                  fontWeight="bold"
                  textAlign="left"
                  w="100%"
                >
                  {watch('title')}
                </Text>
              )}
              <Flex align="center" gap="4px" justify="flex-end" minW="fit-content">
                {withStatus && !editMode && (
                  <Box
                    display="flex"
                    alignItems="center"
                    fontSize="12px"
                    color={statusColor.FontColor}
                    border={`1px solid ${statusColor.BorderColor}`}
                    borderRadius="8px"
                    p="2px 8px"
                    bg={statusColor.BackgroundColor}
                    minW="fit-content"
                    justifyContent="center"
                    role="status"
                    aria-label={`Status: ${getFollowUpQuestionStatusLabel(followUpItem.status)}`}
                  >
                    {getFollowUpQuestionStatusLabel(followUpItem.status)}
                  </Box>
                )}
                {!readonly && !editMode && (
                  <Menu.Root>
                    <Menu.Trigger asChild>
                      <Button
                        variant="ghost"
                        p="0"
                        aria-label="Follow up item actions"
                        loading={isLoading}
                        border="none"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <DotsThree size={24} color="#242424" />
                      </Button>
                    </Menu.Trigger>
                    <Portal>
                      <Menu.Positioner>
                        <Menu.Content>
                          <Menu.Item
                            value="edit"
                            onClick={handleEditClick}
                            cursor="pointer"
                            fontSize="14px"
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Item
                            value="delete"
                            onClick={handleDeleteItem}
                            cursor="pointer"
                            fontSize="14px"
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Portal>
                  </Menu.Root>
                )}
              </Flex>
            </Flex>
            {errors.title && (
              <Text id="title-error" ml="30px" fontSize="14px" color="error.500" role="alert">
                {errors.title.message}
              </Text>
            )}
          </Box>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Stack justifyContent="space-between" h="100%">
            <Stack gap="16px" p="16px 24px" h="100%">
              <Box>
                <Text color="neutrals.900" fontSize="16px" fontWeight="semibold" mb="8px">
                  Issue:
                </Text>
                {editMode ? (
                  <Controller
                    name="alert"
                    control={control}
                    rules={{ required: 'Issue is required' }}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        autoresize
                        fontSize="16px"
                        maxH="80px"
                        bg="background.secondary"
                        borderRadius="8px"
                        borderColor={errors.alert ? 'error.500' : 'neutrals.200'}
                        borderWidth="1px"
                        placeholder="Enter the issue"
                        aria-label="Issue description"
                        aria-invalid={!!errors.alert}
                        aria-describedby={errors.alert ? 'alert-error' : undefined}
                        disabled={isDisabled}
                      />
                    )}
                  />
                ) : (
                  <Text color="neutrals.700" fontSize="16px" fontWeight="normal">
                    {watch('alert')}
                  </Text>
                )}
                {errors.alert && (
                  <Text id="alert-error" fontSize="14px" color="error.500" role="alert">
                    {errors.alert.message}
                  </Text>
                )}
              </Box>

              <Box>
                <Text color="neutrals.900" fontSize="16px" fontWeight="semibold" mb="8px">
                  Follow up questions:
                </Text>
                <Stack align="flex-start" w="100%" gap="12px">
                  {watch('follow_up_questions').map((question, qIndex: number) => (
                    <Flex key={qIndex} w="100%" flexDirection="column">
                      <Flex gap="12px">
                        <Box h="5px" w="5px" bg="neutrals.700" borderRadius="100px" mt="10px" />
                        {editMode ? (
                          <Controller
                            name={`follow_up_questions.${qIndex}.question`}
                            control={control}
                            rules={{ required: 'Question is required' }}
                            render={({ field }) => (
                              <Textarea
                                {...field}
                                flex="1"
                                autoresize
                                fontSize="16px"
                                minH="26px"
                                maxH="56px"
                                bg="background.secondary"
                                borderRadius="12px"
                                placeholder="Add a question"
                                borderColor={
                                  errors.follow_up_questions?.[qIndex]?.question
                                    ? 'error.500'
                                    : 'neutrals.200'
                                }
                                borderWidth="1px"
                                aria-label={`Follow up question ${qIndex + 1}`}
                                aria-invalid={!!errors.follow_up_questions?.[qIndex]?.question}
                                aria-describedby={
                                  errors.follow_up_questions?.[qIndex]?.question
                                    ? `question-error-${qIndex}`
                                    : undefined
                                }
                                disabled={isDisabled}
                              />
                            )}
                          />
                        ) : (
                          <Text flex="1">{question.question}</Text>
                        )}
                        {editMode && (
                          <Flex alignItems="center">
                            <Button
                              variant="ghost"
                              p="0"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isDisabled) return;

                                const updatedQuestions = watch('follow_up_questions').filter(
                                  (_: unknown, i: number) => i !== qIndex
                                );
                                reset({
                                  ...watch(),
                                  follow_up_questions: updatedQuestions
                                });
                              }}
                              aria-label={`Delete question ${qIndex + 1}`}
                              disabled={isDisabled}
                            >
                              <Trash size={18} />
                            </Button>
                          </Flex>
                        )}
                      </Flex>
                      {errors.follow_up_questions?.[qIndex]?.question && (
                        <Text
                          id={`question-error-${qIndex}`}
                          ml="24px"
                          color="error.500"
                          role="alert"
                        >
                          {errors.follow_up_questions[qIndex].question.message}
                        </Text>
                      )}
                    </Flex>
                  ))}
                  {editMode && (
                    <Button
                      variant="ghost"
                      color="primary.900"
                      fontSize="16px"
                      fontWeight="semibold"
                      onClick={addNewQuestion}
                      _hover={{ bg: 'transparent' }}
                      disabled={isDisabled}
                      aria-label="Add a new question"
                    >
                      <Flex align="center" gap="8px">
                        <Plus size={24} weight="bold" />
                        Add a question
                      </Flex>
                    </Button>
                  )}
                </Stack>
              </Box>

              {withFeedback && followUpItem.feedback && (
                <Box
                  border="1px solid"
                  borderColor="primary.100"
                  borderRadius="8px"
                  bg="primary.50"
                  p="16px"
                >
                  <Flex mb="12px" gap="8px">
                    <Image src={aiIcon} w="20px" h="20px" alt="AI Icon" />
                    <Box color="primary.900" fontSize="16px" fontWeight="semibold">
                      AI Feedback
                    </Box>
                  </Flex>
                  <Text color="neutrals.700" fontSize="16px" fontWeight="normal">
                    {followUpItem.feedback}
                  </Text>
                  {suggestUpdateStatus && (
                    <Flex justifyContent="flex-end" mt="12px">
                      <Button
                        variant="ghost"
                        p="0px"
                        color={followUpItem.resolved ? 'primary.900' : 'error.500'}
                        fontSize="14px"
                        fontWeight="semibold"
                        _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          handleEvaluate(
                            followUpItem.resolved
                              ? FollowUpQuestionStatus.Resolved
                              : FollowUpQuestionStatus.NotFit
                          );
                        }}
                        disabled={isDisabled}
                      >
                        Set Status to{' '}
                        {getFollowUpQuestionStatusLabel(
                          followUpItem.resolved
                            ? FollowUpQuestionStatus.Resolved
                            : FollowUpQuestionStatus.NotFit
                        )}
                      </Button>
                    </Flex>
                  )}
                </Box>
              )}
              {submitError && (
                <Text color="error.500" fontSize="16px" role="alert">
                  Failed to update follow up item. Please try again.
                </Text>
              )}

              {editMode && (
                <Flex justifyContent="flex-end" mt="16px" gap="12px">
                  <Button
                    bg="background.secondary"
                    color="primary.900"
                    fontSize="16px"
                    borderRadius="8px"
                    p="8px 38px"
                    border="1px solid"
                    borderColor="primary.900"
                    disabled={isDisabled}
                    onClick={handleCancel}
                    aria-label="Cancel editing"
                  >
                    Cancel
                  </Button>
                  <Button
                    bg="primary.900"
                    color="neutrals.0"
                    fontSize="16px"
                    borderRadius="8px"
                    p="8px 38px"
                    loading={isLoading || isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                    aria-label="Save changes"
                  >
                    Save
                  </Button>
                </Flex>
              )}
            </Stack>
            {evaluate && !editMode && (
              <Stack w="100%" gap="16px" px="24px" borderRadius="8px" bg="neutrals.50">
                <Box borderBottom="1px solid" borderColor="neutrals.200" />
                <Flex gap="4px" justifyContent="space-between">
                  {followUpItem.status !== FollowUpQuestionStatus.Resolved && (
                    <Button
                      variant="ghost"
                      p="16px 12px"
                      border="1px solid"
                      borderColor="transparent"
                      onClick={() => handleEvaluate(FollowUpQuestionStatus.Resolved)}
                      _hover={{
                        bg: 'neutrals.100',
                        borderColor: 'neutrals.800'
                      }}
                      disabled={isDisabled}
                      color="neutrals.800"
                    >
                      <Flex align="center" gap="8px">
                        <Check size={24} weight="bold" />
                        <Text fontSize="16px" fontWeight="semibold">
                          Qualified
                        </Text>
                      </Flex>
                    </Button>
                  )}
                  {followUpItem.status !== FollowUpQuestionStatus.NotFit && (
                    <Button
                      variant="ghost"
                      p="16px 12px"
                      border="1px solid"
                      borderColor="transparent"
                      onClick={() => handleEvaluate(FollowUpQuestionStatus.NotFit)}
                      _hover={{
                        bg: 'neutrals.100',
                        borderColor: 'neutrals.800'
                      }}
                      disabled={isDisabled}
                      color="neutrals.800"
                    >
                      <Flex align="center" gap="8px">
                        <X size={24} weight="bold" />
                        <Text fontSize="16px" fontWeight="semibold">
                          Unmet Criteria
                        </Text>
                      </Flex>
                    </Button>
                  )}
                  {followUpItem.status !== FollowUpQuestionStatus.Pending && (
                    <Button
                      variant="ghost"
                      p="16px 12px"
                      border="1px solid"
                      borderColor="transparent"
                      onClick={() => handleEvaluate(FollowUpQuestionStatus.Pending)}
                      _hover={{
                        bg: 'neutrals.100',
                        borderColor: 'neutrals.800'
                      }}
                      disabled={isDisabled}
                      color="neutrals.800"
                    >
                      <Flex align="center" gap="8px">
                        <ArrowCounterClockwise size={24} weight="bold" />
                        <Text fontSize="16px" fontWeight="semibold">
                          Pending
                        </Text>
                      </Flex>
                    </Button>
                  )}
                </Flex>
              </Stack>
            )}
          </Stack>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}
